/**
 * hydrateNpcDialogue — converts the server `talkToNPC` response (npcDialogue
 * message) into the runtime `NPC` shape that <NPCInteractionUI/> renders.
 *
 * The server only sends quest IDs + a shop-item summary; this helper hydrates
 * quest IDs from QUEST_DATABASE and folds in the player's live quest log so the
 * Quest tab can show Accept vs. Complete correctly. Keeps GameUIOverlay free of
 * data-shaping logic — the overlay just renders the result.
 */

import { abilityStatStrings } from '../../../shared/content/abilityText';
import { abilityText, textCasterOf } from '../components/abilityTooltip';
import type { NPC, Quest as UIQuest, ShopItem as UIShopItem, DialogOption, TrainingOption } from '../../../shared/NPCData';
import { QUEST_DATABASE, QUEST_TURN_IN_MESSAGES } from '../../../shared/content/generated/quests.gen';
import { NPC_CONFIG } from '../../../shared/content/generated/entities.gen';
import type { QuestLog } from '../../../shared/CharacterTypes';
import { questObjectiveTargetName } from '../../../shared/quests/questObjectiveDisplay';
import { questRewardItemsFor } from '../../../shared/quests/questRewardItems';
import { questRewardAbilityId } from '../../../shared/quests/questRewardAbility';
import { resolveQuestEndNpcDisplay } from '../../../shared/quests/questEndNpcDisplay';
import { questPathStates, questPrerequisiteChain, questPrerequisites } from '../../../shared/quests/questChain';
import { isQuestOffered } from '../../../shared/quests/questOffer';
import { isAbilityEligible } from '../../../shared/progression/abilityCurve';
import { abilityRegistry } from '../../systems/player/abilities/AbilityRegistry';
import { abilityIconPath, hasAbilityIcon } from '../../rendering/abilities/AbilityIconRegistry';
import type { ProgressionCostQuoteWire } from '../../../shared/types/ProgressionCost';
import { getAbilityData } from '../../../shared/content/AbilityDataAdapter';
import { npcCompletedQuests, npcQuestGreeting } from '../../../shared/npc/npcQuestLines';
import type { NpcFerryOffer } from '../../../shared/npc/ferryRoutes';

/** Shape of a single shop item inside the server `npcDialogue` payload. */
export interface NpcDialogueShopItem {
  id: string;
  name: string;
  type: string;
  price: number;
  sellPrice?: number;
  icon?: string;
  description?: string;
}

export interface NpcDialogueTrainingOption {
  abilityId: string;
  name: string;
  description: string;
  icon: string;
  requiredLevel: number;
  quote: ProgressionCostQuoteWire;
}

/** Server `talkToNPC` response (the `npcDialogue` message). */
export interface NpcDialoguePayload {
  ok: boolean;
  reason?: string;
  npcId?: string;
  name?: string;
  role?: string;
  dialogue?: { greeting?: string; quests?: string; complete?: string; goodbye?: string };
  quests?: string[];
  shopItems?: NpcDialogueShopItem[];
  trainingOptions?: NpcDialogueTrainingOption[];
  ferryOffer?: NpcFerryOffer;
}

// Server NPC role → UIInteraction `type` (drives header icon/color + tab labels).
const ROLE_TO_TYPE: Record<string, string> = {
  quest_giver: 'quest',
  shop: 'shop',
  trainer: 'trainer',
  guide: 'story',
  guard: 'quest',
  civilian: 'story',
  banker: 'banker',
  ferrymaster: 'story',
};

function toUIQuest(questId: string, npcId: string, questLog?: QuestLog, playerClass?: string): UIQuest | null {
  const def = QUEST_DATABASE[questId];
  if (!def) return null;
  const rewardAbilityId = questRewardAbilityId(def, playerClass);
  const rewardItems = questRewardItemsFor(def, playerClass);
  const rewardClass = playerClass && def.rewardClassItems?.[playerClass.toLowerCase()] ? playerClass.toLowerCase() : undefined;
  const endNpc = resolveQuestEndNpcDisplay(def.endNpc);

  const active = questLog?.active.find(q => q.id === questId);
  const isCompleted = !!questLog?.completed.includes(questId);
  const objectives = def.objectives ?? [];
  // A quest is "turn-in ready" when every objective is met. Talk is satisfied
  // by the turn-in itself; explore/kill/collect compare server-projected counts.
  const atEndNpc = npcId === def.endNpc;
  const turnInReady = !!active && atEndNpc && objectives.every((o, i) => {
    if (o.type === 'talk') return o.targetId === npcId;
    const cur = active.objectives?.[i]?.currentCount
      ?? (i === 0 ? (active.currentCount ?? 0) : 0);
    return cur >= o.targetCount;
  });

  const uiObjectives = objectives.map((objective, i) => {
    const current = active?.objectives?.[i]?.currentCount
      ?? (i === 0 ? (active?.currentCount ?? 0) : 0);
    return {
      type: objective.type,
      targetId: objective.targetId,
      targetName: questObjectiveTargetName(objective.type, objective.targetId, objective.targetCount),
      description: objective.description,
      // A talk objective is met by talking to its target, which is who this card belongs to
      // (the same rule as turnInReady above), so it reads complete rather than 0 / 1.
      current: isCompleted || (objective.type === 'talk' && !!active && objective.targetId === npcId)
        ? objective.targetCount : current,
      required: objective.targetCount,
    };
  });

  return {
    id: def.id,
    title: def.name,
    description: def.description,
    endNpcId: endNpc.npcId ?? undefined,
    endNpcName: endNpc.name,
    isAccepted: !!active,
    isCompleted,
    isTurnInReady: turnInReady,
    objectives: uiObjectives,
    requiredLevel: def.requiredLevel,
    rewardXP: def.rewards.xp,
    rewardGold: def.rewards.gold,
    // Shared items plus the viewing class's class_items entry (lane AO).
    rewardItemDetails: rewardItems.map(({ id, name, quantity }) => ({ id, name, quantity })),
    rewardItems: rewardItems.map(i => `${i.name}${i.quantity > 1 ? ` x${i.quantity}` : ''}`),
    ...(rewardClass ? { rewardClass } : {}),
    prerequisites: def.requiredQuests ? [...questPrerequisites(def)] : undefined,
    ...(def.featured ? {
      featured: true,
      path: questPathStates(questPrerequisiteChain(questId).slice(0, -1),
        new Set(questLog?.completed ?? []), new Set((questLog?.active ?? []).map(q => q.id))),
    } : {}),
    ...(def.guidance ? { guidance: def.guidance } : {}),
    ...(rewardAbilityId ? {
      rewardAbility: {
        id: rewardAbilityId, name: getAbilityData(rewardAbilityId)?.name ?? rewardAbilityId,
        ...(def.rewardAbilityHidden && !isCompleted ? { hidden: true } : {}),
      },
    } : {}),
  };
}

/**
 * One quest as the quest card sees it, judged at its turn-in NPC (plan 02:
 * the quest tracker's "show on map" reuses the card's <QuestGuidance>).
 */
export function questCardView(questId: string, questLog?: QuestLog, playerClass?: string): UIQuest | null {
  // Own ids only: 'constructor' or '__proto__' (an unknown id in an old save) is not a quest.
  const def = Object.prototype.hasOwnProperty.call(QUEST_DATABASE, questId) ? QUEST_DATABASE[questId] : undefined;
  return def ? toUIQuest(questId, def.endNpc ?? '', questLog, playerClass) : null;
}

/**
 * Featured quests the NPC authors but the server did not offer (locked by
 * prerequisite or level), not yet accepted or finished. Display-only teasers.
 */
function projectFeaturedTeasers(npcId: string, offered: readonly UIQuest[], questLog?: QuestLog, playerClass?: string): UIQuest[] {
  const shown = new Set(offered.map(q => q.id));
  const done = new Set(questLog?.completed ?? []);
  const active = new Set((questLog?.active ?? []).map(q => q.id));
  return (NPC_CONFIG[npcId]?.availableQuests ?? [])
    .filter(id => QUEST_DATABASE[id]?.featured && isQuestOffered(QUEST_DATABASE[id]) && !shown.has(id) && !active.has(id)
      && (QUEST_DATABASE[id].repeatable || !done.has(id)))
    .map(id => toUIQuest(id, npcId, questLog, playerClass))
    .filter((q): q is UIQuest => q !== null);
}

function toUIShopItem(item: NpcDialogueShopItem): UIShopItem {
  return {
    id: item.id,
    name: item.name,
    price: item.price,
    type: item.type,
    rarity: 'common',
    stats: {},
    description: item.description ?? '',
    currency: 'gold',
  };
}

/**
 * Build the runtime NPC the interaction UI renders from the server response.
 * Returns null when the server reported the dialogue could not open.
 */
export function hydrateNpcDialogue(
  payload: NpcDialoguePayload,
  playerLevel: number,
  questLog?: QuestLog,
  playerClass?: string,
  learnedAbilities: readonly string[] = [],
  /** The reader's character, for the trainer's stat lines (plan 29 F2). */
  trainee?: Parameters<typeof textCasterOf>[0],
): NPC | null {
  if (!payload.ok || !payload.npcId) return null;

  const quests: UIQuest[] = (payload.quests ?? [])
    .map(id => toUIQuest(id, payload.npcId!, questLog, playerClass))
    .filter((q): q is UIQuest => q !== null);

  // Also surface accepted quests that belong to this NPC but are no longer in
  // the "available" list (so the player can turn in talk/explore quests here).
  for (const active of questLog?.active ?? []) {
    if (quests.some(q => q.id === active.id)) continue;
    const def = QUEST_DATABASE[active.id];
    if (!def || (def.endNpc && def.endNpc !== payload.npcId)) continue;
    const ui = toUIQuest(active.id, payload.npcId!, questLog, playerClass);
    if (ui) quests.push(ui);
  }

  const shopItems: UIShopItem[] = (payload.shopItems ?? []).map(toUIShopItem);
  const featuredTeasers = projectFeaturedTeasers(payload.npcId, quests, questLog, playerClass);

  const readyQuest = quests.find(q => q.isTurnInReady);
  const completedQuests = npcCompletedQuests(payload.npcId, questLog);
  const options: DialogOption[] = [];

  // A ready turn-in speaks first; then the line keyed by this reader's quest
  // state (plan 23 row 44); then the NPC's plain greeting.
  let greeting = (readyQuest && QUEST_TURN_IN_MESSAGES[readyQuest.id])
    || npcQuestGreeting(payload.npcId, questLog, payload.dialogue?.greeting ?? 'Hello, traveler.');

  // Pip and Mallow gesture in the portrait once the lesson is done (batch-003 gesture pose).
  let portraitPose: 'talk' | 'gesture' | undefined;
  // Specialize Pip Reedwhistle and Mallow Wick dialogue state machines (HNR-B01 - HNR-B08)
  if (payload.npcId === 'pip_reedwhistle') {
    const isPipQuestActive = questLog?.active.some(q => q.id === 'somewhere_the_road_is_quiet') ?? false;
    const isPipQuestCompleted = questLog?.completed.includes('somewhere_the_road_is_quiet') ?? false;

    if (isPipQuestCompleted) {
      portraitPose = 'gesture';
      greeting = 'He taught you! I knew he would. Now we both know where to find you: probably two steps farther along than we expected.';
      options.push({ text: 'Thanks, Pip. Safe travels.', action: 'close' });
    } else if (isPipQuestActive) {
      greeting = 'Three round windows. Crooked blue ribbon. The reeds on the Drowned Road are hiding something much smaller than a monster.';
      options.push({
        text: 'Give me a clearer hint.',
        action: 'pip_hint',
        speech: 'Stay with the road until you find an old boathouse gone green under a broken stone arch. Our lantern hangs by its door; step up to the reeds in the doorway and use {key:interact}.',
      });
      options.push({ text: 'I\'ll keep looking.', action: 'close' });
    } else {
      greeting = 'You\'re going down the long road, aren\'t you? Mallow says it feels shorter if you know where the quiet bits are.';
      options.push({
        text: 'Is he in trouble?',
        action: 'pip_not_in_trouble',
        speech: 'No! He took the good cushion and both cups. That\'s how I know he\'s having a very successful hiding.',
      });
      options.push({
        text: 'Maybe another time.',
        action: 'pip_decline',
        speech: 'That\'s all right. It\'s a good hiding place. It can wait.',
      });
    }
  } else if (payload.npcId === 'mallow_wick') {
    const hasLearnedLanternskip = learnedAbilities.includes('lanternskip');
    const isMallowQuestCompleted = questLog?.completed.includes('somewhere_the_road_is_quiet') ?? false;
    const isMallowQuestActive = questLog?.active.some(q => q.id === 'somewhere_the_road_is_quiet') ?? false;
    const isColeMet = Boolean(questLog?.completed.includes('the_fog_inland') || questLog?.completed.includes('clear_the_brambleway')
      || questLog?.completed.includes('whats_in_the_mist') || questLog?.completed.includes('the_hollow_keeper'));

    if (isMallowQuestCompleted) {
      portraitPose = 'gesture';
      if (!isColeMet) {
        greeting = 'Two little steps for when one feels too ordinary. The stones are flat if you want to try. The road will still be there afterward. Cole is farther along the road—you can borrow a few steps on the way.';
      } else {
        greeting = 'The opening there takes you back to the same bit of road. Try not to skip past anyone saying hello.';
      }
      options.push({
        text: 'Tell me about the souls you saw.',
        action: 'mallow_lore',
        speech: 'You can tell a tired traveler by the pause before the next step. That\'s why I put the cushion where they can see it.',
      });
      options.push({ text: 'I\'ll be on my way.', action: 'close' });
    } else if (isMallowQuestActive) {
      // Sent by Pip (Pip-first)
      if (hasLearnedLanternskip) {
        greeting = 'You already know the shape of it. Good! Then you can help me decide whether the second skip looks better with a little turn.';
        options.push({ text: 'Complete lesson.', action: 'complete_quest:somewhere_the_road_is_quiet' });
        options.push({
          text: 'Tell me about the souls you saw.',
          action: 'mallow_lore',
          speech: 'One soul stopped to fix the ribbon. I remember that one better than the ones who hurried.',
        });
        options.push({ text: 'I\'ll sit for a moment.', action: 'close' });
      } else {
        // Truthful active lesson state (QER-03): neutral copy that never invents a prior Pip conversation.
        greeting = 'Come in. The road is loud up there; down here you can hear the little things. Souls have been passing this little window for a long time. I watched their feet until I understood the small bit: don\'t hurry the whole road. Just borrow the next step.';
        options.push({ text: 'Teach me Lanternskip.', action: 'complete_quest:somewhere_the_road_is_quiet' });
        options.push({
          text: 'Tell me about the souls you saw.',
          action: 'mallow_lore',
          speech: 'You can tell a tired traveler by the pause before the next step. That\'s why I put the cushion where they can see it.',
        });
        options.push({ text: 'I\'ll sit for a moment.', action: 'close' });
      }
    } else {
      // Found-first arrival!
      greeting = 'Oh. You found the curtain without Pip explaining it all? He\'ll be terribly impressed. You\'re welcome to sit. Or I can show you something that makes long roads less long.';
      if (hasLearnedLanternskip) {
        options.push({ text: 'I already know Lanternskip.', action: 'accept_quest:somewhere_the_road_is_quiet' });
        options.push({
          text: 'Tell me about the souls you saw.',
          action: 'mallow_lore',
          speech: 'One soul stopped to fix the ribbon. I remember that one better than the ones who hurried.',
        });
        options.push({ text: 'I\'ll sit for a moment.', action: 'close' });
      } else {
        options.push({ text: 'Teach me your trick.', action: 'accept_quest:somewhere_the_road_is_quiet' });
        options.push({
          text: 'Tell me about the souls you saw.',
          action: 'mallow_lore',
          speech: 'You can tell a tired traveler by the pause before the next step. That\'s why I put the cushion where they can see it.',
        });
        options.push({ text: 'I\'ll sit for a moment.', action: 'close' });
      }
    }
  }

  if (payload.role === 'banker') {
    options.push({ text: 'Access Bank Vault', action: 'bank', icon: '💰' });
  }
  if (payload.role === 'ferrymaster' && payload.ferryOffer?.actionId === 'board_ferry'
    && typeof payload.ferryOffer.label === 'string' && payload.ferryOffer.label.trim()) {
    options.push({ text: payload.ferryOffer.label, ferryAction: payload.ferryOffer.actionId, icon: '⛵' });
  }
  const learned = new Set(learnedAbilities);
  const trainingOptions: TrainingOption[] | undefined = payload.role === 'trainer'
    ? (payload.trainingOptions ?? [])
      .filter(option => !learned.has(option.abilityId) && option.requiredLevel <= playerLevel
        && (!playerClass || isAbilityEligible(playerClass, playerLevel, option.abilityId)))
      .map(option => {
        // Presentation metadata cannot create an offer or replace its server quote.
        const authored = abilityRegistry.getAll().find(ability => ability.id === option.abilityId);
        // Plan 29 F2: the TOML description and the stat line from content.
        return { ...option,
          description: abilityText(option.abilityId, authored?.description?.trim()) || option.description,
          stats: abilityStatStrings(option.abilityId, textCasterOf(trainee ?? { level: playerLevel, class: playerClass })),
          icon: hasAbilityIcon(option.abilityId) ? abilityIconPath(option.abilityId) : (authored?.icon || option.icon) };
      })
    : undefined;
  // Retain the old service projection for non-structured consumers. The trainer
  // panel renders TrainingView exclusively when trainingOptions is present.
  const services = (trainingOptions ?? [])
      .map(option => ({
        name: option.name,
        description: option.description,
        icon: option.icon,
        cost: option.quote.cost,
        action: `learnAbility:${option.abilityId}`,
      }));

  return {
    id: payload.npcId,
    name: payload.name ?? payload.npcId,
    level: playerLevel,
    type: ROLE_TO_TYPE[payload.role ?? ''] ?? 'story',
    dialog: { greeting, options },
    quests,
    shopItems,
    services,
    ...(trainingOptions !== undefined ? { trainingOptions } : {}),
    ...(featuredTeasers.length > 0 ? { featuredTeasers } : {}),
    ...(completedQuests.length > 0 ? { completedQuests } : {}),
    ...(portraitPose ? { portraitPose } : {}),
  };
}

/** Featured chapters this conversation showed (teasers plus offered featured quests). Recording them is
 *  the caller's job (`useRecordDiscoveredQuests`), per character; hydration itself has no side effects. */
export function featuredQuestIds(npc: Pick<NPC, 'quests' | 'featuredTeasers'>): string[] {
  return [...(npc.featuredTeasers ?? []).map(q => q.id), ...npc.quests.filter(q => q.featured).map(q => q.id)];
}
